import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { DASHBOARD_HERO_ACTIONS, ROUTE_PATHS } from '../config/navigation';
import {
  getCorporateContentFallback,
  hydrateCorporateContent
} from '../data/corporateContent';
import { getSelfServiceContentFallback, hydrateSelfServiceContent } from '../data/selfServiceContent';
import { ORGANIZATION_BRAND } from '../config/organization';

const fallbackAnnouncements = [
  {
    title: 'İş Sağlığı ve Güvenliği duyurusu',
    content: 'Güncellenen İSG prosedürleri doküman merkezinde yayımlandı.',
    createdAt: new Date().toISOString(),
    author: { firstName: 'Koloğlu', lastName: 'Holding' }
  },
  {
    title: 'Yıl sonu etkinlik takvimi paylaşımı',
    content: 'Yıl sonu organizasyon planı ve katılım detayları etkinlik takviminde yayımlandı.',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    author: { firstName: 'Kurumsal', lastName: 'İletişim' }
  }
];

const getCurrentLunchMenu = (menuItems) => {
  if (!menuItems.length) {
    return null;
  }

  const weekdayIndex = new Date().getDay();
  const weekdayMap = {
    1: 'Pazartesi',
    2: 'Salı',
    3: 'Çarşamba',
    4: 'Perşembe',
    5: 'Cuma'
  };

  const todayMenu = menuItems.find((item) => item.day === weekdayMap[weekdayIndex]);
  return todayMenu || menuItems[0];
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [corporateContent, setCorporateContent] = useState(() => getCorporateContentFallback());
  const [selfServiceContent, setSelfServiceContent] = useState(() => getSelfServiceContentFallback());
  const [dashboardData, setDashboardData] = useState({
    announcements: [],
    statistics: {
      totalUsers: 0,
      totalAnnouncements: 0,
      totalDocuments: 0,
      totalProjects: 0
    },
    birthdays: []
  });

  const activeCompanyId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      if (user.role === 'super_admin') {
        return localStorage.getItem('activeCompanyId') || '';
      }
      return user.companyId || '';
    } catch (error) {
      return '';
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [countData, managedContent] = await Promise.all([
          api.getCounts(),
          api.getManagedContent()
        ]);

        const statistics = countData.data?.statistics || {
          totalUsers: 0,
          totalAnnouncements: 0,
          totalDocuments: 0,
          totalProjects: 0
        };

        setCorporateContent(
          managedContent?.success
            ? hydrateCorporateContent(managedContent.data?.corporate || {})
            : getCorporateContentFallback()
        );
        setSelfServiceContent(
          managedContent?.success
            ? hydrateSelfServiceContent(managedContent.data?.selfService || {})
            : getSelfServiceContentFallback()
        );

        setDashboardData({
          announcements:
            countData.data?.latestAnnouncements?.length > 0
              ? countData.data.latestAnnouncements
              : fallbackAnnouncements,
          statistics,
          birthdays: countData.data?.recentBirthdays || []
        });
      } catch (error) {
        console.error('Dashboard verileri yüklenirken hata:', error);
        setCorporateContent(getCorporateContentFallback());
        setSelfServiceContent(getSelfServiceContentFallback());
        setDashboardData({
          announcements: fallbackAnnouncements,
          statistics: {
            totalUsers: 0,
            totalAnnouncements: fallbackAnnouncements.length,
            totalDocuments: 0,
            totalProjects: 0
          },
          birthdays: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [activeCompanyId]);

  const openLink = (path, external = false) => {
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(path);
  };

  const managementMessages = corporateContent.managementMessages || [];
  const cultureHighlights = corporateContent.cultureHighlights || [];
  const participationLinks = corporateContent.participationLinks || [];
  const corporateQuickAccess = corporateContent.corporateQuickAccess || [];
  const quickContacts = corporateContent.quickContacts || [];
  const systemLinks = corporateContent.systemLinks || [];
  const operationsContacts = selfServiceContent.operationsContacts || [];
  const techFaqs = selfServiceContent.techFaqs || [];
  const currentLunchMenu = getCurrentLunchMenu(selfServiceContent.lunchMenu || []);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-dashboard corporate-dashboard">
      <div className="row mb-4">
        <div className="col-12">
          <div className="corporate-hero-card corporate-hero-card-expanded">
            <div className="corporate-hero-intro">
              <span className="corporate-hero-badge">Hızlı Erişim</span>
              <h1>Günlük ihtiyaçlarınıza ve kurumsal kaynaklara tek tıkla ulaşın</h1>
              <p>
                Yemekhane menüsü, izin ve masraf talepleri, IT destek, dokümanlar, personel rehberi
                ve diğer sık kullanılan alanlar aşağıdan seçilebilir.
              </p>
            </div>
            <div className="corporate-hero-actions corporate-hero-actions-grid">
              {DASHBOARD_HERO_ACTIONS.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className={`corporate-hero-link-btn${action.variant === 'primary' ? ' is-primary' : ''}`}
                  onClick={() => openLink(action.path, action.external)}
                >
                  <i className={action.icon} aria-hidden="true"></i>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-lg-6 mb-4 mb-lg-0">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-message-detail"></i>
                <h4>Yönetimden Mesajlar</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="corporate-message-list">
                {managementMessages.map((message) => (
                  <div key={message.id} className="corporate-message-card">
                    <span className="corporate-pill">{message.badge}</span>
                    <h5>{message.title}</h5>
                    <p>{message.summary}</p>
                    <div className="corporate-message-footer">
                      <div>
                        <strong>{message.author}</strong>
                        <small>{message.role}</small>
                      </div>
                      <button
                        className="btn btn-link text-decoration-none px-0"
                        onClick={() => navigate(message.ctaPath)}
                      >
                        {message.ctaLabel}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-megaphone"></i>
                <h4>Merkezi Duyurular</h4>
              </div>
              <button className="modern-widget-action" onClick={() => navigate(ROUTE_PATHS.announcements)}>
                <i className="bx bx-right-arrow-alt"></i>
              </button>
            </div>
            <div className="modern-widget-body">
              {dashboardData.announcements.length > 0 ? (
                <div className="modern-activity-list">
                  {dashboardData.announcements.slice(0, 4).map((announcement, index) => (
                    <div key={index} className="modern-activity-item">
                      <div className="modern-activity-icon">
                        <i className="bx bxs-megaphone"></i>
                      </div>
                      <div className="modern-activity-content">
                        <h6 className="modern-activity-title">{announcement.title}</h6>
                        <p className="modern-activity-meta">
                          {new Date(announcement.createdAt).toLocaleDateString('tr-TR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}{' '}
                          - {announcement.author?.firstName} {announcement.author?.lastName}
                        </p>
                        {announcement.content && (
                          <p className="modern-activity-description">
                            {announcement.content.length > 120
                              ? `${announcement.content.substring(0, 120)}...`
                              : announcement.content}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modern-empty-state">
                  <i className="bx bxs-megaphone"></i>
                  <p>Henüz duyuru bulunmuyor</p>
                </div>
              )}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default DashboardPage;
